import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { SharePlatform } from "@/utils/share-intents";

export interface TeamSharePrefs {
  share_platform: SharePlatform;
  share_group_url: string | null;
  telegram_chat_id: number | null;
  telegram_chat_title: string | null;
}

const DEFAULT_PREFS: TeamSharePrefs = {
  share_platform: "whatsapp",
  share_group_url: null,
  telegram_chat_id: null,
  telegram_chat_title: null,
};

/**
 * Staff-only hook: reads teamvote_team_share_prefs for a team.
 * RLS guarantees only the coach or active collaborators receive data.
 * Non-staff viewers silently get the default ("whatsapp", no group URL).
 */
export function useTeamSharePrefs(teamId: string | null | undefined) {
  const [prefs, setPrefs] = useState<TeamSharePrefs>(DEFAULT_PREFS);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);
    const { data } = await supabase
      .from("teamvote_team_share_prefs")
      .select("share_platform, share_group_url, telegram_chat_id, telegram_chat_title")
      .eq("team_id", teamId)
      .maybeSingle();
    if (data) {
      const row = data as {
        share_platform: string | null;
        share_group_url: string | null;
        telegram_chat_id: number | null;
        telegram_chat_title: string | null;
      };
      setPrefs({
        share_platform: (row.share_platform as SharePlatform) ?? "whatsapp",
        share_group_url: row.share_group_url ?? null,
        telegram_chat_id: row.telegram_chat_id ?? null,
        telegram_chat_title: row.telegram_chat_title ?? null,
      });
    } else {
      setPrefs(DEFAULT_PREFS);
    }
    setLoading(false);
  }, [teamId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { prefs, loading, refresh };
}
